import { useState } from 'react'
import { User, Bell, Shield, Loader2, LogOut } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Label,
  Switch,
} from '@/components/ui'
import { LoadingSpinner, ConfirmDialog } from '@/components/shared'
import { useProfile } from '@/hooks'
import { useAuthStore } from '@/stores'
import { useAuth } from '@/hooks/useAuth'
import type { NotificationPreferences, UpdateProfileRequest } from '@/types'

type SettingsTab = 'profile' | 'notifications' | 'security'

const TABS: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
]

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
  const { profile, isLoading } = useProfile()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your account and preferences</p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="max-w-2xl">
        {activeTab === 'profile' && <ProfileSection />}
        {activeTab === 'notifications' && (
          <NotificationsSection preferences={profile?.notification_preferences} />
        )}
        {activeTab === 'security' && <SecuritySection />}
      </div>
    </div>
  )
}

function ProfileSection() {
  const { user } = useAuthStore()
  const { updateProfile, isUpdating } = useProfile()
  const [firstName, setFirstName] = useState(user?.first_name ?? '')
  const [lastName, setLastName] = useState(user?.last_name ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Reset form when user changes
  const [prevUserId, setPrevUserId] = useState(user?.id)
  if (user?.id !== prevUserId) {
    setPrevUserId(user?.id)
    setFirstName(user?.first_name ?? '')
    setLastName(user?.last_name ?? '')
    setPhone(user?.phone ?? '')
  }

  const hasChanges =
    firstName !== (user?.first_name ?? '') ||
    lastName !== (user?.last_name ?? '') ||
    phone !== (user?.phone ?? '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required')
      return
    }

    try {
      const data: UpdateProfileRequest = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
      }
      await updateProfile(data)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>Update your personal information</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={user?.email ?? ''} disabled />
            <p className="mt-1 text-xs text-gray-500">
              Email cannot be changed. Contact support if you need to update it.
            </p>
          </div>

          <div>
            <Label htmlFor="phone">Phone number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
            <p className="mt-1 text-xs text-gray-500">
              Required for SMS notifications
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-error-50 p-3 text-sm text-error-700">{error}</div>
          )}

          {success && (
            <div className="rounded-md bg-success-50 p-3 text-sm text-success-700">
              Profile updated successfully
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={isUpdating || !hasChanges}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function NotificationsSection({ preferences }: { preferences?: NotificationPreferences }) {
  const { updateProfile, isUpdating } = useProfile()
  const [push, setPush] = useState(preferences?.push ?? true)
  const [email, setEmail] = useState(preferences?.email ?? true)
  const [sms, setSms] = useState(preferences?.sms ?? false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Reset when preferences change
  const [prevPrefs, setPrevPrefs] = useState(preferences)
  if (preferences !== prevPrefs) {
    setPrevPrefs(preferences)
    setPush(preferences?.push ?? true)
    setEmail(preferences?.email ?? true)
    setSms(preferences?.sms ?? false)
  }

  const hasChanges =
    push !== (preferences?.push ?? true) ||
    email !== (preferences?.email ?? true) ||
    sms !== (preferences?.sms ?? false)

  const handleSave = async () => {
    setError('')
    setSuccess(false)

    try {
      await updateProfile({
        notification_preferences: { push, email, sms },
      })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update preferences')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>Choose how you want to receive alerts and updates</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Push Notifications</div>
              <div className="text-sm text-gray-500">
                Receive push notifications on your mobile device
              </div>
            </div>
            <Switch checked={push} onCheckedChange={setPush} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Email Notifications</div>
              <div className="text-sm text-gray-500">
                Receive important alerts and summaries via email
              </div>
            </div>
            <Switch checked={email} onCheckedChange={setEmail} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">SMS Notifications</div>
              <div className="text-sm text-gray-500">
                Receive critical alerts via text message
              </div>
            </div>
            <Switch checked={sms} onCheckedChange={setSms} />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-error-50 p-3 text-sm text-error-700">{error}</div>
        )}

        {success && (
          <div className="rounded-md bg-success-50 p-3 text-sm text-success-700">
            Preferences updated successfully
          </div>
        )}

        <div className="flex justify-end border-t pt-4">
          <Button onClick={handleSave} disabled={isUpdating || !hasChanges}>
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function SecuritySection() {
  const { signOut } = useAuth()
  const { deleteAccount, isDeleting } = useProfile()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const handleDeleteAccount = async () => {
    setDeleteError('')
    try {
      await deleteAccount()
      // Sign out after deletion
      await signOut()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account')
      setShowDeleteConfirm(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch {
      // Sign out errors are handled by the auth hook
    }
  }

  return (
    <div className="space-y-6">
      {/* Session */}
      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
          <CardDescription>Manage your current session</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>Change your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Password management is handled through your sign-in provider. If you signed up with
            email, you can reset your password from the login page.
          </p>
          <Button variant="outline" asChild>
            <a href="/forgot-password">Reset Password</a>
          </Button>
        </CardContent>
      </Card>

      {/* Delete account */}
      <Card className="border-error-200">
        <CardHeader>
          <CardTitle className="text-error-700">Delete Account</CardTitle>
          <CardDescription>Permanently delete your account and all data</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Once you delete your account, there is no going back. All your data will be
            permanently removed. This action cannot be undone.
          </p>

          {deleteError && (
            <div className="rounded-md bg-error-50 p-3 text-sm text-error-700 mb-4">
              {deleteError}
            </div>
          )}

          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete your account?"
        description="This action cannot be undone. All your data, including family memberships, will be permanently deleted."
        confirmLabel="Delete Account"
        variant="destructive"
        onConfirm={handleDeleteAccount}
        loading={isDeleting}
      />
    </div>
  )
}
